import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import HeroShowcase from "../components/HeroShowcase";
import ServiceSceneRow from "../components/ServiceSceneRow";
import { ReviewCards } from "../components/Ratings";
import { MascotWelcome } from "../components/Mascot";
import Marquee from "../components/Marquee";
import { Reveal, MaskLines } from "../lib/motion";
import { useRequestDialog } from "../components/RequestDialog";
import { api } from "../lib/api";
import { usePageMeta } from "../lib/seo";
import { BUSINESS, HOME_DEFAULT, HOME_FAQS, SERVICES } from "../content/defaults";

const BENEFITS = [
  { heading: "Tell us the symptoms", text: "Share what's happening in your own words.", icon: "chat" },
  { heading: "Keep your vehicle history", text: "Upload records so the shop sees the bigger picture.", icon: "file" },
  { heading: "Stay informed", text: "Get updates as your request is reviewed.", icon: "bell" },
];

const STEPS = [
  { heading: "Describe the problem", text: "Tell us what your vehicle is doing and when it started." },
  { heading: "Request a time", text: "Choose a date that works for you." },
  { heading: "Hear from the shop", text: "Your request is sent to the shop for confirmation." },
];

const BOOKING_FAQS = [
  ...HOME_FAQS,
  { q: "Can I share my VIN?", a: "Yes. The VIN is optional in the repair form (up to 17 characters). It lets staff pull up the exact visit history for your vehicle — that history stays with shop staff only." },
];

function BenefitIcon({ icon }) {
  if (icon === "chat") return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 8.5-8.5 8.38 8.38 0 0 1 8.5 8.5z"/></svg>;
  if (icon === "file") return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/></svg>;
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0"/></svg>;
}

export default function Home() {
  const openDialog = useRequestDialog();
  const [content, setContent] = useState(HOME_DEFAULT);
  const [book, setBook] = useState({ name: "", contact: "", vehicle: "", date: "", symptoms: "" });

  usePageMeta("C.C.C. Auto Electric | Electrical Repair in Hawaiian Gardens",
    "Auto electrical repair in Hawaiian Gardens. Battery & charging, wiring & lighting, starter & alternator. Describe your repair and request an appointment — call (562) 425-2027.");

  useEffect(() => {
    api("/content/home").then((d) => setContent({ ...HOME_DEFAULT, ...d.content })).catch(() => {});
  }, []);

  const continueRequest = (e) => {
    e.preventDefault();
    const parts = (book.vehicle || "").trim().split(/\s+/);
    const year = parts.length > 2 && /^\d{4}$/.test(parts[0]) ? parts[0] : "";
    const rest = year ? parts.slice(1) : parts;
    const make = rest.length > 1 ? rest[0] : rest[0] || "";
    const model = rest.length > 1 ? rest.slice(1).join(" ") : "";
    const isPhone = /^[+\d][\d\s\-()]{6,}$/.test(book.contact.trim());
    openDialog("Electrical diagnosis", book.symptoms, true, {
      first_name: book.name.trim().split(/\s+/)[0] || "",
      last_name: book.name.trim().split(/\s+/).slice(1).join(" "),
      phone: isPhone ? book.contact : "",
      email: isPhone ? "" : book.contact,
      year, make, model,
      preferred_date: book.date,
    });
  };

  const bookService = (svc, describe = false) => (e) => {
    openDialog(svc.primary.service, "", !describe);
  };

  return (
    <main>
      <section className="hero">
        <div className="container">
          <MascotWelcome
            text="Glad you're here! Tell us what your vehicle is doing, explore our electrical services, or request an appointment. I'll help you collect the details for the shop."
            onCta={() => openDialog("Electrical diagnosis", "", true)} />
          <h1 data-testid="hero-headline">
            <MaskLines lines={["Auto electrical repair", "in Hawaiian Gardens"]} />
          </h1>
          <Reveal delay={0.55}>
            <HeroShowcase />
            <p className="hero-sub">
              Your vehicle's electrical system keeps you moving. Explore the interactive animation above, then tell us
              what your vehicle is doing or request an appointment. The more details you share, the easier it is for the shop to help.
            </p>
            <div className="row-actions" style={{ justifyContent: "center", marginTop: 20 }}>
              <button className="btn btn-blue" onClick={() => openDialog("Electrical diagnosis", "", true)} data-testid="book-diagnostic-cta-button">
                Request an Appointment <ArrowRight size={15} />
              </button>
              <button className="btn btn-ghost" onClick={() => openDialog("Electrical diagnosis")} data-testid="describe-repair-cta-button">
                Describe My Repair <ArrowRight size={15} />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section pale-section" style={{ marginTop: 60 }}>
        <div className="container">
          <div className="section-head" style={{ marginBottom: 26 }}>
            <h2 data-testid="intro-heading">You know the symptoms. We make it easier to explain them.</h2>
            <p>Share what's happening with your vehicle in your own words. We'll collect the details and get them to the shop.</p>
          </div>
          <ReviewCards />
        </div>
      </section>

      <Marquee />

      <section className="section">
        <div className="container">
          {SERVICES.map((s, i) => (
            <ServiceSceneRow key={s.id} service={s} sceneSide={i % 2 === 0 ? "left" : "right"}
              onBook={(describe) => openDialog(s.primary.service, "", !describe)} />
          ))}
        </div>
      </section>

      <section className="section pale-section">
        <div className="container">
          <Reveal>
            <div className="section-head">
              <h2>A local shop. A clearer repair conversation.</h2>
              <p>We make it simple to share what's going on with your vehicle. Tell us the symptoms, keep important details in one place, and stay informed as the shop reviews your request.</p>
            </div>
          </Reveal>
          <div className="bento" data-testid="benefit-grid">
            {BENEFITS.map((b, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="card">
                  <span className="icon"><BenefitIcon icon={b.icon} /></span>
                  <h4>{b.heading}</h4>
                  <p>{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <Reveal>
            <div className="section-head">
              <span className="eyebrow red">Three steps</span>
              <h2>How to request help</h2>
            </div>
          </Reveal>
          <div className="steps" data-testid="process-steps">
            {STEPS.map((s, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="step">
                  <span className="n">{i + 1}</span>
                  <div><h4>{s.heading}</h4><p>{s.text}</p></div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section pale-section">
        <div className="container">
          <Reveal>
            <div className="section-head">
              <span className="eyebrow red">Before you visit</span>
              <h2>Common questions</h2>
            </div>
          </Reveal>
          <div style={{ maxWidth: 780, margin: "0 auto" }} data-testid="home-faq-list">
            {BOOKING_FAQS.map((f, i) => (
              <details className="faq" key={i} data-testid={`home-faq-item-${i}`}>
                <summary>{f.q}<span className="chev">+</span></summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <Reveal>
            <div className="card" style={{ padding: "clamp(26px,4vw,48px)" }} data-testid="booking-strip">
              <div className="booking-strip">
                <div>
                  <h2>Tell us what your vehicle is doing.</h2>
                  <p className="muted" style={{ lineHeight: 1.7 }}>Fill out a few details and we'll send your request to the shop.</p>
                  <p className="form-note" style={{ marginTop: 18 }}>Call {BUSINESS.phoneDisplay} · {BUSINESS.address}</p>
                </div>
                <form onSubmit={continueRequest} data-testid="quick-booking-form">
                  <div className="field-pair">
                    <div className="field"><label>Name *</label>
                      <input required value={book.name} onChange={(e) => setBook({ ...book, name: e.target.value })} placeholder="Your name" data-testid="quick-name-input" />
                    </div>
                    <div className="field"><label>Phone or email *</label>
                      <input required value={book.contact} onChange={(e) => setBook({ ...book, contact: e.target.value })} placeholder="(562) 555-0123 or you@email.com" data-testid="quick-contact-input" />
                    </div>
                  </div>
                  <div className="field-pair">
                    <div className="field"><label>Vehicle *</label>
                      <input required value={book.vehicle} onChange={(e) => setBook({ ...book, vehicle: e.target.value })} placeholder="Year Make/Model" data-testid="quick-vehicle-input" />
                    </div>
                    <div className="field"><label>Preferred date</label>
                      <input type="date" value={book.date} onChange={(e) => setBook({ ...book, date: e.target.value })} data-testid="quick-date-input" />
                    </div>
                  </div>
                  <div className="field"><label>Symptoms *</label>
                    <textarea required rows={3} value={book.symptoms} onChange={(e) => setBook({ ...book, symptoms: e.target.value })} placeholder="Describe what's happening…" data-testid="quick-symptoms-input" />
                  </div>
                  <button className="btn btn-blue" style={{ width: "100%" }} data-testid="quick-continue-button">
                    Continue to Repair Request <ArrowRight size={15} />
                  </button>
                  <p className="form-note" style={{ marginTop: 10 }}>Appointment requests are confirmed by the shop.</p>
                </form>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      <section style={{ padding: "60px 0 0" }}>
        <Reveal>
          <img src="/storefront-welcome.jpg"
            alt="Visit C.C.C. Auto Electric at 12430 E Carson St, Hawaiian Gardens, CA — call (562) 425-2027 or see www.cccautoelectric.com. The shop mascot waves from out front."
            style={{ width: "100%", display: "block" }} data-testid="storefront-welcome" />
        </Reveal>
      </section>
    </main>
  );
}
